import { useState } from "react";
import axios from "axios";
import { ArrowRight, Diamond } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD =
  "border-white/15 bg-white/[0.04] text-white placeholder:text-[#717680] focus-visible:ring-[#C9A24B]/50 focus-visible:border-[#C9A24B]/60";

export const RequestInventoryModal = ({ open, onOpenChange, interest = "Inventory request" }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        interest,
        message: form.message || "Please send the current inventory list.",
      });
      toast.success("Request received — the inventory list is on its way to your inbox.");
      setForm({ name: "", email: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not send the request — please email contact@sunraysorg.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="request-inventory-modal"
        className="glass-panel max-w-md border-white/15 bg-[#141418]/90 text-white sm:rounded-3xl"
      >
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full border border-[#C9A24B]/40 bg-[#C9A24B]/10">
            <Diamond className="h-5 w-5 text-[#C9A24B]" strokeWidth={1.5} />
          </div>
          <DialogTitle className="font-display text-2xl font-light text-white">
            {interest === "Inventory request" ? "Request the current inventory" : "Get an exact quote"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#B8BCC2]">
            Trade only. Lists are sent from the desk within one business day.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="request-inventory-form">
          <Input
            data-testid="request-inventory-name"
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className={FIELD}
          />
          <Input
            data-testid="request-inventory-email"
            required
            type="email"
            placeholder="Work email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={FIELD}
          />
          <Textarea
            data-testid="request-inventory-message"
            rows={4}
            placeholder="What are you looking for? (shapes, sizes, certs, timeline)"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            className={FIELD}
          />
          <button data-testid="request-inventory-submit" type="submit" disabled={sending} className="gold-btn w-full disabled:opacity-60">
            {sending ? "Sending…" : "Send Request"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
